import { useMyMonitoringGetUnitTriggers } from '@apps-common/apis/my-monitoring/triggers'
import { TriggerListCard, TriggerListCardSkeleton } from '@apps-common/apps/portal/pages/units/pages/manage/pages/triggers/components'
import { TriggerModifyModal } from '@apps-common/apps/portal/pages/units/pages/manage/pages/triggers/modals/trigger-modify-modal/TriggerModifyModal'
import { NoResultsAddCard, PullRefresh } from '@apps-common/components/common'
import { PageSubtitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundLg, ButtonRoundSm } from '@apps-common/components/elements'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_STATUS_ACTIVE } from '@apps-common/config/unit-filter'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { useMyMonitoringSiteContextStore } from '@apps-common/stores'
import { IMMSiteModel } from '@apps-common/types/my-monitoring'
import { useIonModal } from '@ionic/react'

interface IProps {
  loading: boolean
  site?: IMMSiteModel
  back: () => void
}

export const SiteTriggerView: React.FC<IProps> = ({ loading, site, back }) => {
  const { isAdmin } = useMyMonitoringSiteContextStore()

  const { data, isLoading, isError, refetch } = useMyMonitoringGetUnitTriggers({
    config: {
      enabled: !!site?.unitId
    },
    unitId: site?.unitId || ''
  })

  const [present, dismiss] = useIonModal(TriggerModifyModal, {
    componentProps: {
      status: UNIT_STATUS_ACTIVE.value,
      unitId: site?.unitId,
      productId: site?.productId
    },
    onDismiss: (data: string, role: string) => dismiss(data, role),
    enableBackdropDismiss: true
  })

  loading = loading || !data || isLoading || isError

  return (
    <>
      <PullRefresh loading={loading} callback={refetch} />
      <div className=' flex flex-col gap-4 max-w-[430px] lg:max-w-full mx-auto'>
        <div className='flex items-center justify-between gap-4'>
          <div className='flex items-center justify-start gap-4'>
            <ButtonRoundSm
              type='button'
              className='bg-ctx-mm-brand text-ctx-on-mm-brand'
              callback={() => {
                if (back) {
                  back()
                }
              }}
            >
              <commonIcons.arrowRight className='rotate-180' />
            </ButtonRoundSm>
            <PageSubtitle classes='text-text'>Control Triggers</PageSubtitle>
          </div>
          {isAdmin && (
            <ModalButton
              component={TriggerModifyModal}
              disabled={loading}
              componentProps={{
                status: UNIT_STATUS_ACTIVE.value,
                unitId: site?.unitId,
                productId: site?.productId
              }}
              enterAnimation={modalSlideUpEnterAnimation}
              leaveAnimation={modalSlideUpLeaveAnimation}
              classes='relative'
            >
              <ButtonRoundLg
                type='div'
                className='action-success'
                tooltip={{ id: 'device-triggers-add-new', place: 'top', message: 'New trigger' }}
              >
                <commonIcons.add className='!text-[1.1rem]' />
              </ButtonRoundLg>
            </ModalButton>
          )}
        </div>
        <div className='rounded-[35px] grid lg:grid-cols-2 gap-4'>
          {loading && Array.from(Array(2).keys()).map((k) => <TriggerListCardSkeleton key={`trigger-list-card-skeleton-${k}`} />)}
          {!loading &&
            data?.map((d) => (
              <TriggerListCard
                key={`trigger-list-card-${d.unitTriggerId}`}
                unitStatus={UNIT_STATUS_ACTIVE.value as string}
                unitId={site?.unitId as string}
                productId={site?.productId as string}
                trigger={d}
                disabled={!isAdmin}
              />
            ))}
          {!loading && data?.length === 0 && (
            <NoResultsAddCard
              title='Nothing to see here'
              message='This site has no triggers.'
              callback={() =>
                present({
                  enterAnimation: modalSlideUpEnterAnimation,
                  leaveAnimation: modalSlideUpLeaveAnimation
                })
              }
              disabled={loading || !isAdmin}
            />
          )}
        </div>
      </div>
    </>
  )
}
